import React, { useMemo, useState } from "react";
import { Button, Input } from "reactstrap";
import notifications from "../../../common/Components/Notifications/Notification";
import { recordPayEntry } from "../../../services/karkhanaApi";
import { rupees } from "../rates/rateModel";
import { newRequestId } from "../rounds/entryQueue";
import { checkPayout, defaultPayout, METHODS } from "./payModel";
import { useKT } from "../i18n/kt";

export type MoneyMode = "pay" | "advance";

/**
 * Pay wages (cutting the agreed advance recovery first) or give an advance. Money needs signal:
 * nothing is kept on the phone, so a payment is never recorded twice or half-recorded silently.
 */
const MoneySheet = ({
  mode,
  account,
  onClose,
  onSaved,
}: {
  mode: MoneyMode;
  account: any;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const { t } = useKT();
  const start = useMemo(() => defaultPayout(account.wagesDue, account.suggestedRecovery), [account]);
  const [cut, setCut] = useState(start.cut);
  const [handOver, setHandOver] = useState(start.handOver);
  const [amount, setAmount] = useState("");
  const [perPay, setPerPay] = useState(account.agreedRecovery || "");
  const [method, setMethod] = useState("CASH");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const person = account.person;
  const owed = Number(account.advanceOutstanding || 0);
  const error = mode === "pay" ? checkPayout(account.wagesDue, account.advanceOutstanding, cut || "0", handOver || "0") : undefined;

  const post = async (body: Record<string, unknown>) => {
    const res: any = await recordPayEntry({ ...body, personId: person.id, clientRequestId: newRequestId() });
    if (!res?.success) throw new Error(t(res?.message || "Not saved"));
  };

  const save = async () => {
    setBusy(true);
    let cutSaved = false;
    try {
      if (mode === "pay") {
        if (error) {
          notifications({ title: t("Check the amounts"), message: error, type: "warning" });
          return;
        }
        // The cut first, so the payment is checked against what is left.
        if (Number(cut) > 0) {
          await post({ kind: "RECOVERY", amount: cut, note: note || undefined });
          cutSaved = true;
        }
        if (Number(handOver) > 0) await post({ kind: "PAYMENT", amount: handOver, method, note: note || undefined });
        notifications({ title: t("Saved"), message: `${person.code}: ${t("paid {{amount}}", { amount: rupees(handOver) })}${Number(cut) > 0 ? `, ${t("cut {{amount}}", { amount: rupees(cut) })}` : ""}`, type: "success" });
      } else {
        await post({ kind: "ADVANCE", amount, recoveryPerPay: perPay, method, note: note || undefined });
        notifications({ title: t("Saved"), message: `${person.code}: ${t("advance {{amount}}", { amount: rupees(amount) })}`, type: "success" });
      }
      onSaved();
    } catch (e: any) {
      const offline = !(e instanceof Error) || /network|fetch/i.test(e.message);
      const why = offline ? t("Money needs a connection; try again.") : t(e.message);
      notifications({
        title: cutSaved ? t("Payment not saved") : t("Not saved"),
        message: cutSaved ? t("The cut of {{amount}} was recorded, but the payment wasn't: {{why}}", { amount: rupees(cut), why }) : why,
        type: "danger",
      });
      onSaved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="kr-sheet-backdrop" onClick={onClose} />
      <section className="kr-sheet" role="dialog" aria-modal="true" aria-label={mode === "pay" ? t("Pay wages") : t("Give advance")}>
        <header className="kr-sheet__head">
          <h3>{person.code}</h3>
          <span className="kw-muted kp-head-name">{mode === "pay" ? t("Pay wages") : t("Give advance")}</span>
          <button type="button" className="kr-iconbtn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="kr-sheet__body">
          <dl className="kr-who">
            <dt>{t("Wages due")}</dt>
            <dd>{rupees(account.wagesDue)}</dd>
            <dt>{t("Advance owed")}</dt>
            <dd>
              {rupees(account.advanceOutstanding)}
              {account.agreedRecovery ? ` · ${rupees(account.agreedRecovery)} cut each payout` : ""}
            </dd>
          </dl>

          {mode === "pay" ? (
            <>
              {owed > 0 ? (
                <>
                  <label className="kr-label" htmlFor="kpay-cut">
                    {t("Cut for advance")}
                  </label>
                  <Input id="kpay-cut" inputMode="decimal" value={cut} onChange={(e) => setCut(e.target.value)} />
                </>
              ) : null}
              <label className="kr-label" htmlFor="kpay-hand">
                {t("Hand over")}
              </label>
              <Input id="kpay-hand" inputMode="decimal" value={handOver} onChange={(e) => setHandOver(e.target.value)} />
              {error ? <p className="kr-pay is-missing mt-2">{error}</p> : null}
            </>
          ) : (
            <>
              <label className="kr-label" htmlFor="kpay-adv">
                {t("Advance amount")}
              </label>
              <Input id="kpay-adv" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
              <label className="kr-label" htmlFor="kpay-per">
                {t("Agreed cut at each payout")}
              </label>
              <Input id="kpay-per" inputMode="decimal" value={perPay} onChange={(e) => setPerPay(e.target.value)} placeholder="e.g. 500" />
              <p className="kw-muted mt-2">{t("Only this much is cut each time they are paid, until the advance is repaid.")}</p>
            </>
          )}

          <span className="kr-label">{t("Given as")}</span>
          <div className="kr-chips" role="radiogroup" aria-label="Method">
            {METHODS.map((m) => (
              <button key={m.key} type="button" role="radio" aria-checked={method === m.key} className={`kr-choice${method === m.key ? " is-on" : ""}`} onClick={() => setMethod(m.key)}>
                {t(m.label)}
              </button>
            ))}
          </div>
          <label className="kr-label" htmlFor="kpay-note">
            {t("Note (optional)")}
          </label>
          <Input id="kpay-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <footer className="kr-sheet__foot">
          <Button outline disabled={busy} onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button className="ResetBtn" disabled={busy || (mode === "pay" ? !!error : !amount.trim() || !String(perPay).trim())} onClick={save}>
            {busy ? t("Saving…") : mode === "pay" ? t("Pay {{amount}}", { amount: rupees(handOver || 0) }) : t("Give {{amount}}", { amount: rupees(amount || 0) })}
          </Button>
        </footer>
      </section>
    </>
  );
};

export default MoneySheet;
